import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Home, Loader2, ChevronRight, Vault as VaultIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export function TenantChooser({ onChosen }) {
  const { refreshUser, logout } = useAuth();
  const [families, setFamilies] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/families"); setFamilies(data); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setFamilies([]); }
    })();
  }, []);

  const choose = async (fam) => {
    setBusy(fam.id);
    try {
      await api.post("/families/switch", { family_id: fam.id });
      sessionStorage.setItem("fv_tenant_confirmed", "1");
      onChosen && onChosen();
      await refreshUser();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(null); }
  };

  return (
    <div className="min-h-screen px-7 py-14 relative z-10" data-testid="tenant-chooser">
      <div className="h-14 w-14 rounded-2xl grid place-items-center gold-border mb-5"
        style={{ background: "var(--bg-elevated)" }}>
        <VaultIcon size={24} className="gold-text" />
      </div>
      <h1 className="font-serif-display text-4xl leading-none">Choose a family</h1>
      <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
        You belong to more than one family vault. Pick which one to open.
      </p>

      {!families ? (
        <div className="grid place-items-center py-16"><Loader2 className="animate-spin gold-text" /></div>
      ) : (
        <div className="mt-8 space-y-3">
          {families.map((f) => (
            <motion.button key={f.id} whileTap={{ scale: 0.98 }} onClick={() => choose(f)} disabled={!!busy}
              data-testid={`tenant-option-${f.id}`}
              className="w-full card-surface rounded-2xl p-4 flex items-center justify-between text-left">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl grid place-items-center gold-border" style={{ background: "var(--bg-elevated)" }}>
                  <Home size={20} className="gold-text" />
                </div>
                <div>
                  <div className="font-semibold">{f.name}</div>
                  <div className="text-xs capitalize" style={{ color: "var(--text-muted)" }}>{f.role}</div>
                </div>
              </div>
              {busy === f.id ? <Loader2 size={18} className="animate-spin gold-text" /> : <ChevronRight size={18} style={{ color: "var(--text-muted)" }} />}
            </motion.button>
          ))}
        </div>
      )}

      <button onClick={logout} className="mt-8 text-sm" style={{ color: "var(--text-muted)" }} data-testid="tenant-chooser-logout">Log out</button>
    </div>
  );
}
